import { createContext, useContext, useMemo, ReactNode } from 'react';
import useAxios from 'axios-hooks';
import { Local } from '../model/Local';

export interface Sucursal {
  localidad: Localidad;
  latitud: string;
  longitud: string;
  idSucursal: string;
  direccion: string;
}

export interface Comercio {
  cuit: number;
  imagen: string;
  nombreFantasia: string;
  instagram: string;
  rubro: Rubro;
  sucursales: Sucursal[];
}

export interface Rubro {
  id: number;
  nombre: string;
}

export interface Localidad {
  nombre: string;
}

interface AppContextValue {
  comercios: Comercio[];
  rubros: Rubro[];
  localidades: Localidad[];
  loading: boolean;
  error: boolean;
}

const AppContext = createContext<AppContextValue>({
  comercios: [],
  rubros: [],
  localidades: [],
  loading: true,
  error: false,
});

export const useAppContext = () => useContext(AppContext);

function parseBuscadorResponse(data: Local[]) {
  const rubrosMap = new Map<number, string>();
  const localidadesSet = new Set<string>();
  const comercioMap = new Map<number, Comercio>();

  for (const local of data) {
    if (local.rubro && local.idRubro != null) rubrosMap.set(local.idRubro, local.rubro);
    if (local.localidad) localidadesSet.add(local.localidad);

    if (!comercioMap.has(local.cuit)) {
      comercioMap.set(local.cuit, {
        cuit: local.cuit,
        imagen: local.imagen ?? '',
        nombreFantasia: local.nombreFantasia ?? '',
        instagram: local.instagram ?? '',
        rubro: { id: local.idRubro ?? 0, nombre: local.rubro ?? '' },
        sucursales: [],
      });
    }

    // Si el backend devuelve el comercio sin sucursal (idSucursal/direccion en null),
    // no fabrico un item vacio — el comercio aparece sin el apartado "Sucursales".
    if (local.idSucursal != null && local.direccion != null) {
      comercioMap.get(local.cuit)!.sucursales.push({
        idSucursal: local.idSucursal,
        direccion: local.direccion,
        localidad: { nombre: local.localidad ?? '' },
        latitud: local.latitud ?? '',
        longitud: local.longitud ?? '',
      });
    }
  }

  const rubros: Rubro[] = [...rubrosMap.entries()].sort(([, a], [, b]) => a.localeCompare(b)).map(([id, nombre]) => ({ id, nombre }));
  const localidades: Localidad[] = [...localidadesSet].sort().map((nombre) => ({ nombre }));
  const comercios: Comercio[] = [...comercioMap.values()];

  return { comercios, rubros, localidades };
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [{ data: raw, loading, error }] = useAxios<Local[]>('/comercio/buscador');

  const { comercios, rubros, localidades } = useMemo(
    () => (raw ? parseBuscadorResponse(raw) : { comercios: [], rubros: [], localidades: [] }),
    [raw]
  );

  const value = useMemo(
    () => ({ comercios, rubros, localidades, loading, error: !!error }),
    [comercios, rubros, localidades, loading, error]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
